import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  AccountMembershipResponseV1,
  AddFeatureToOptionRequestV1,
  AddOptionToPackageRequestV1,
  AllocatePackageToUserRequestV1,
  CreateFeatureRequestV1,
  CreateOptionRequestV1,
  CreatePackageRequestV1,
  FeatureResponseV1,
  GroupMembershipResponseV1,
  MembershipResponseV1,
  OptionResponseV1,
  PackageResponseV1,
  PackagesSummaryV1
} from '@membership/membership';
import { catchError, map, Observable, of, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/apis';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MembershipService {
  private readonly http = inject(HttpClient);
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };
  private readonly packagesApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/packages`;
  private readonly optionsApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/options`;
  private readonly featuresApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/features`;
  private readonly adminStatsApiUrl = `${environment.apiUrl}${environment.apis.iamEngine}/v1/stats`;

  private handleResourceError<T>(err: unknown): Observable<never> {
    const error =
      err instanceof HttpErrorResponse
        ? new Error(`HTTP ${err.status}: ${err.message}`)
        : err instanceof Error
          ? err
          : new Error(String(err));
    return throwError(() => error);
  }

  // Resource factories - components create their own instances
  createPackagesResource() {
    return rxResource<PackageResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<PackageResponseV1[]>>(this.packagesApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createOptionsResource() {
    return rxResource<OptionResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<OptionResponseV1[]>>(this.optionsApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createFeaturesResource() {
    return rxResource<FeatureResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<FeatureResponseV1[]>>(this.featuresApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createPackagesSummaryResource() {
    return rxResource<PackagesSummaryV1 | null, void>({
      stream: () =>
        this.http.get<ServiceResponseV1<PackagesSummaryV1>>(`${this.adminStatsApiUrl}/packages`).pipe(
          map((response) => response.data ?? null),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createGroupMembershipsResource(groupId: () => string | null) {
    return rxResource<GroupMembershipResponseV1 | null, string | null>({
      params: () => groupId(),
      stream: ({ params }) => {
        if (!params) return of(null);
        return this.http
          .get<
            ServiceResponseV1<GroupMembershipResponseV1>
          >(`${environment.apiUrl}${environment.apis.iamEngine}/v1/groups/${params}/memberships`)
          .pipe(
            map((response) => response.data ?? null),
            catchError((err) => this.handleResourceError(err))
          );
      }
    });
  }

  /**
   * @deprecated Allocation now flows through invitation accept. Kept to keep
   * accounts-table/users-table compiling until those flows are refactored.
   */
  createAccountMembershipsResource(accountId: () => string | null) {
    return rxResource<AccountMembershipResponseV1 | null, void>({
      stream: () => {
        const id = accountId();
        if (!id) {
          return new Observable<AccountMembershipResponseV1 | null>((subscriber) => {
            subscriber.next(null);
            subscriber.complete();
          });
        }
        return this.http
          .get<
            ServiceResponseV1<AccountMembershipResponseV1>
          >(`${environment.apiUrl}${environment.apis.iamEngine}/v1/accounts/${id}/memberships`)
          .pipe(
            map((response) => response.data!),
            catchError((err) => this.handleResourceError(err))
          );
      }
    });
  }

  // ==================== Packages ====================
  getPackageById(packageId: string): Observable<PackageResponseV1> {
    return this.http
      .get<ServiceResponseV1<PackageResponseV1>>(`${this.packagesApiUrl}/${packageId}`)
      .pipe(map((response) => response.data!));
  }

  createPackage(pkg: CreatePackageRequestV1): Observable<PackageResponseV1> {
    return this.http
      .post<ServiceResponseV1<PackageResponseV1>>(`${this.packagesApiUrl}`, pkg, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  updatePackage(packageId: string, pkg: Partial<PackageResponseV1>): Observable<PackageResponseV1> {
    return this.http
      .put<ServiceResponseV1<PackageResponseV1>>(`${this.packagesApiUrl}/${packageId}`, pkg, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  deletePackage(packageId: string): Observable<void> {
    return this.http.delete<void>(`${this.packagesApiUrl}/${packageId}`);
  }

  getPackageOptions(packageId: string): Observable<OptionResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<OptionResponseV1[]>>(`${this.packagesApiUrl}/${packageId}/options`)
      .pipe(map((response) => response.data || []));
  }

  addOptionsToPackage(request: AddOptionToPackageRequestV1): Observable<void> {
    return this.http.post<void>(`${this.packagesApiUrl}/${request.packageId}/addOptions`, request, this.httpOptions);
  }

  // ==================== Options ====================
  getOptionById(optionId: string): Observable<OptionResponseV1> {
    return this.http
      .get<ServiceResponseV1<OptionResponseV1>>(`${this.optionsApiUrl}/${optionId}`)
      .pipe(map((response) => response.data!));
  }

  createOption(option: CreateOptionRequestV1): Observable<OptionResponseV1> {
    return this.http
      .post<ServiceResponseV1<OptionResponseV1>>(`${this.optionsApiUrl}`, option, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  updateOption(optionId: string, option: Partial<OptionResponseV1>): Observable<OptionResponseV1> {
    return this.http
      .put<ServiceResponseV1<OptionResponseV1>>(`${this.optionsApiUrl}/${optionId}`, option, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  deleteOption(optionId: string): Observable<void> {
    return this.http.delete<void>(`${this.optionsApiUrl}/${optionId}`);
  }

  getOptionFeatures(optionId: string): Observable<FeatureResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<FeatureResponseV1[]>>(`${this.optionsApiUrl}/${optionId}/features`)
      .pipe(map((response) => response.data || []));
  }

  addFeaturesToOption(request: AddFeatureToOptionRequestV1): Observable<void> {
    return this.http.post<void>(`${this.optionsApiUrl}/${request.optionId}/addFeatures`, request, this.httpOptions);
  }

  // ==================== Features ====================
  getFeatureById(featureId: string): Observable<FeatureResponseV1> {
    return this.http
      .get<ServiceResponseV1<FeatureResponseV1>>(`${this.featuresApiUrl}/${featureId}`)
      .pipe(map((response) => response.data!));
  }

  createFeature(feature: CreateFeatureRequestV1): Observable<FeatureResponseV1> {
    return this.http
      .post<ServiceResponseV1<FeatureResponseV1>>(`${this.featuresApiUrl}`, feature, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  updateFeature(featureId: string, feature: Partial<FeatureResponseV1>): Observable<FeatureResponseV1> {
    return this.http
      .put<ServiceResponseV1<FeatureResponseV1>>(`${this.featuresApiUrl}/${featureId}`, feature, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  deleteFeature(featureId: string): Observable<void> {
    return this.http.delete<void>(`${this.featuresApiUrl}/${featureId}`);
  }

  // ==================== Memberships (deprecated) ====================
  /**
   * @deprecated Allocation now flows through invitation accept. The corresponding
   * backend endpoint may have been removed; calls will fail until the consumers
   * (accounts-table/users-table) are refactored to use the invitation flow.
   */
  allocatePackageToUser(accountId: string, request: AllocatePackageToUserRequestV1): Observable<MembershipResponseV1> {
    return this.http
      .post<
        ServiceResponseV1<MembershipResponseV1>
      >(`${environment.apiUrl}${environment.apis.iamEngine}/v1/accounts/${accountId}/memberships`, request, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  /** @deprecated See allocatePackageToUser. */
  getAccountMemberships(accountId: string): Observable<AccountMembershipResponseV1> {
    return this.http
      .get<
        ServiceResponseV1<AccountMembershipResponseV1>
      >(`${environment.apiUrl}${environment.apis.iamEngine}/v1/accounts/${accountId}/memberships`)
      .pipe(map((response) => response.data!));
  }
}
